import {
  Button,
  CheckBox,
  FieldGroup,
  FieldRow,
  ModalDialog,
  Panel,
} from "react-corner-ds";
import { useTranslation, Trans } from "react-i18next";
import style from "./InformationRecap.module.css";

import { useCallback, useEffect, useMemo, useState } from "react";
import { store } from "../../../redux/store";
import { reformatDate } from "../../../utils/reformat";

import {
  CountriesResponse,
  CountryItem,
} from "../../../models/response/response.model";
import i18n from "../../../i18n";
import PersonalData from "../personalData/PersonalData";
import { Request } from "../../../models/request/request.model";
import { useFlow } from "../../../hooks/useFlow/useFlow";
import { maritalItems, occupationItems } from "../../../assets/locales/utils";
import { Bank } from "../../../models/bank/bank.model";
import PersonalDataAdditional from "../personalDataAdditional/PersonalDataAdditional";
import Address from "../address/Address";
import EmploymentDetails from "../employmentDetails/EmploymentDetails";
import { mockBank } from "../../../assets/mock/mock";

const InformationRecap = () => {
  const { t } = useTranslation();

  const [nextStepLoading, setNextStepLoading] = useState<boolean>(false);
  const [isOpenFirstPanel, setIsOpenFirstPanel] = useState<boolean>(false);
  const [bank, setBank] = useState<Bank>();
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const { callSteploader } = useFlow();
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isShowGenericModal, setIsShowGenericModal] = useState<boolean>(false);
  const [isDisclaimer, setIsDisclaimer] = useState<boolean>(false);
  const [selectedModal, setSelectedModal] = useState<string>("");

  useEffect(() => {
    setIsOpenFirstPanel(true);
  }, []);

  useEffect(() => {
    import(`../../../assets/countries/${i18n.resolvedLanguage}.json`).then(
      (resp: CountriesResponse) => {
        setCountries(resp.countries);
      }
    );
  }, [i18n.resolvedLanguage]);

  const buildMaritalStatus = useCallback(
    (code: string | undefined) =>
      code && maritalItems(t).find((item) => item.value === code)?.label,
    [t]
  );

  const buildCountry = useCallback(
    (code: string | undefined) =>
      code && countries.find((item) => item.alpha_2 === code)?.name,
    [countries]
  );

  const buildOccupation = useCallback(
    (code: string | undefined) =>
      code && occupationItems(t).find((item) => item._value === code)?.text,
    [t]
  );

  const buildEditButton = useCallback(
    (dataModal: string) => (
      <i
        className={style.edit}
        onClick={() => {
          setIsShowModal(true);
          setSelectedModal(dataModal);
        }}
        data-modal={dataModal}
      >
        <i className="pencil-icon" />
      </i>
    ),
    []
  );

  const submitHandler = async () => {
    let body: Request = {
      Data: {
        FlowDirection: "Next",
        Bank: {
          DisclaimerAccepted: isDisclaimer,
        },
      },
      Extra: {},
    };

    setNextStepLoading(true);
    await callSteploader(body);
  };

  useEffect(() => {
    if (!store.getState().appConf.workflowEnabled) {
      setBank(mockBank);
    } else {
      setBank(store.getState().pega.Data?.Bank);
    }
  }, [store.getState().pega.Data?.Bank]);

  const buildPhoneNumber = useMemo(() => {
    return bank?.Customer?.Phones?.map((item, index) => (
      <p key={"phone" + index}>{item?.Number}</p>
    ));
  }, [bank?.Customer?.Phones]);

  const hideModal = useCallback((ev: any) => {
    ev.preventDefault();
    setIsShowModal(false);
    setIsShowGenericModal(false);
  }, []);

  const personalDataContent = useMemo(
    () =>
      bank?.Customer && (
        <div>
          <h4>
            {!store.getState().pega?.Data?.Bank?.Customer?.KYC &&
              buildEditButton("PersonalData")}
          </h4>
          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.gender`}</span>
              {bank?.Customer.Salutation === "001"
                ? t`screens.common.mr`
                : t`screens.common.mrs`}
            </div>
            <div className={style.customItem}>
              <div className={style.void}></div>
            </div>
          </div>

          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.firstName`}</span>
              {bank?.Customer.FirstName}
            </div>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.lastName`}</span>
              {bank?.Customer.LastName}
            </div>
          </div>

          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.email`}</span>
              {bank?.Customer.Email}
            </div>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.phoneNr`}</span>
              {buildPhoneNumber}
            </div>
          </div>
        </div>
      ),
    [bank?.Customer, t, buildEditButton, buildPhoneNumber]
  );

  const personalDataAdditionalContent = useMemo(
    () =>
      bank?.Customer && (
        <div>
          <h4>{buildEditButton("PersonalDataAdditional")}</h4>
          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.birthDate`}</span>
              {reformatDate(
                bank?.Customer.DateOfBirth,
                "YYYYMMDD",
                "DD.MM.YYYY"
              )}
            </div>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.birthCountry`}</span>
              <div className={style.customItem}>
                {buildCountry(bank?.Customer.PlaceOfBirth)}
              </div>
            </div>
          </div>

          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.nationality`}</span>
              {buildCountry(bank?.Customer.Nationality)}
            </div>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.maritalStatus`}</span>
              {buildMaritalStatus(bank?.Customer.MaritalStatus)}
            </div>
          </div>

          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.avs`}</span>
              {bank?.Customer.SocialSecurityNumber}
            </div>
          </div>
        </div>
      ),
    [bank?.Customer, buildEditButton, t, buildMaritalStatus, buildCountry]
  );

  const addressContent = useMemo(
    () =>
      bank?.Customer?.Addresses && (
        <div>
          <h4>
            {!store.getState().pega?.Data?.Bank?.Customer?.KYC &&
              buildEditButton("Address")}
          </h4>
          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.city`}</span>
              {bank.Customer.Addresses[0].City &&
                bank.Customer.Addresses[0].City.toLowerCase()
                  .charAt(0)
                  .toUpperCase() +
                  bank.Customer.Addresses[0].City.toLowerCase().slice(1)}
            </div>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.zipCode`}</span>
              {bank.Customer.Addresses[0]?.ZipCode}
            </div>
          </div>

          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.street`}</span>
              {bank.Customer.Addresses[0]?.Street}
            </div>
            {!store.getState().appConf.crossSelling && (
              <div className={style.customItem}>
                <span>{t`screens.informationRecap.streetNumber`}</span>
                {bank.Customer.Addresses[0]?.StreetNumber}
              </div>
            )}
          </div>
        </div>
      ),
    [buildEditButton, bank?.Customer, t]
  );

  const employmentDetailsContent = useMemo(
    () =>
      bank?.Customer && (
        <div>
          <h4>{buildEditButton("EmploymentDetails")}</h4>
          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.currentPosition`}</span>
              {buildOccupation(bank.Customer.FinancialInformation?.Occupation)}
            </div>
            <div className={style.customItem}>
              <div className={style.customItem}>
                <span>{t`screens.informationRecap.qualification`}</span>
                {bank.Customer.FinancialInformation?.Qualification}
              </div>
            </div>
          </div>

          <div className={style.customRow}>
            <div className={style.customItem}>
              <span>{t`screens.informationRecap.pensionFund`}</span>
              {bank.Customer.LPP ? t`screens.common.yes` : t`screens.common.no`}
            </div>
          </div>
        </div>
      ),
    [bank?.Customer, buildEditButton, t, buildOccupation]
  );

  const termsInput = useMemo(
    () => (
      <FieldRow customCssClass={style.disclaimerRow}>
        <FieldGroup customCssClass={style.disclaimerCheckBox}>
          <CheckBox
            name={"terms"}
            customCssClass={style.terms}
            onChange={(ev) => setIsDisclaimer(ev.target.checked)}
          />
        </FieldGroup>
        <FieldGroup customCssClass={style.disclaimer}>
          <Trans
            i18nKey={t`screens.informationRecap.terms`}
            components={{
              Terms: (
                <a
                  href={
                    store.getState().appConf.environment === "test" ||
                    store.getState().appConf.environment === "prep"
                      ? t`screens.informationRecap.termsLink_prep`
                      : t`screens.informationRecap.termsLink_prod`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {}
                </a>
              ),
              GeneralTerms: (
                <a
                  href={
                    store.getState().appConf.environment === "test" ||
                    store.getState().appConf.environment === "prep"
                      ? t`screens.informationRecap.generalTermsLink_prep`
                      : t`screens.informationRecap.generalTermsLink_prod`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {}
                </a>
              ),
            }}
          />
        </FieldGroup>
      </FieldRow>
    ),
    [t]
  );

  const buildInfoModal = useMemo(() => {
    return (
      <div>
        <h1>{t`screens.informationRecap.infoModal.title`}</h1>
        <div>
          <p>
            <Trans
              i18nKey={t`screens.informationRecap.infoModal.description`}
            />
          </p>
        </div>
        <div>
          <Button
            customCssClass={style.understoodButton}
            text={t`screens.informationRecap.infoModal.understood`}
            onClick={() => setIsShowModal(false)}
          />
        </div>
      </div>
    );
  }, [t]);

  const buildDisclaimerModal = useMemo(() => {
    return (
      <div>
        <h1>{t`screens.informationRecap.disclaimerModal.title`}</h1>
        <div>
          <p>
            <Trans
              i18nKey={t`screens.informationRecap.disclaimerModal.description`}
            />
          </p>
        </div>
        <div>
          <Button
            customCssClass={style.understoodButton}
            text={t`screens.informationRecap.disclaimerModal.understood`}
            onClick={() => setIsShowModal(false)}
          />
        </div>
      </div>
    );
  }, [t]);

  const buildModal = useMemo(() => {
    switch (selectedModal) {
      case "PersonalData":
        return (
          <PersonalData
            modalRecap
            modalCallback={() => setIsShowModal(false)}
            mockObject={mockBank}
          />
        );

      case "PersonalDataAdditional":
        return (
          <PersonalDataAdditional
            modalRecap
            modalCallback={() => setIsShowModal(false)}
            mockObject={mockBank}
          />
        );

      case "Address":
        return (
          <Address
            modalRecap
            modalCallback={() => setIsShowModal(false)}
            mockObject={mockBank}
          />
        );

      case "EmploymentDetails":
        return (
          <EmploymentDetails
            modalRecap
            modalCallback={() => setIsShowModal(false)}
            mockObject={mockBank}
          />
        );

      case "Info":
        return buildInfoModal;

      case "Disclaimer":
        return buildDisclaimerModal;

      default:
        break;
    }
  }, [selectedModal]);

  const buildGenericModal = useMemo(() => {
    switch (selectedModal) {
      case "Info":
        return buildInfoModal;

      case "Disclaimer":
        return buildDisclaimerModal;

      default:
        break;
    }
  }, [selectedModal]);

  return (
    <>
      <div className="container">
        <h1>{t`screens.informationRecap.pageTitle`}</h1>
        <h2>{t`screens.informationRecap.pageSubtitle`}</h2>
        <div className="content">
          <Panel
            title={t`screens.informationRecap.personalData` || ""}
            expanded={isOpenFirstPanel}
          >
            <div className={style.cats}>
              {personalDataContent}
              {personalDataAdditionalContent}
              {addressContent}
            </div>
          </Panel>

          <Panel
            title={t`screens.informationRecap.employmentsDetail` || ""}
            expanded={isOpenFirstPanel}
          >
            <div className={style.cats}>{employmentDetailsContent}</div>
          </Panel>

          {termsInput}

          <div className={style.btnHolder}>
            <Button
              text={t`screens.informationRecap.cta`}
              onClick={submitHandler}
              icon={
                nextStepLoading
                  ? { icon: "loader", position: "right", spin: true }
                  : undefined
              }
              disabled={!isDisclaimer || nextStepLoading}
            />
          </div>

          <div className={style.infoChanges}>
            <p>{t`screens.informationRecap.dataChanges`}</p>
          </div>
        </div>
      </div>

      <ModalDialog
        id={selectedModal}
        show={isShowModal}
        handleClose={hideModal}
        backdropClose={true}
        customCssClass={style.updateModal}
      >
        {buildModal}
      </ModalDialog>

      <ModalDialog
        id={selectedModal}
        show={isShowGenericModal}
        handleClose={hideModal}
        backdropClose={true}
        customCssClass={style.genericModal}
      >
        {buildGenericModal}
      </ModalDialog>
    </>
  );
};

export default InformationRecap;
